<template>
  <div v-if="visible" class="vista-previa" role="status">
    <div class="vista-previa__texto">
      <span class="vista-previa__punto" aria-hidden="true"></span>
      <strong>Vista previa</strong>
      <span>{{ explicacion }}</span>
      <span class="vista-previa__cuenta">{{ cuenta }}</span>
    </div>

    <a class="vista-previa__salir" :href="salida" @click="salir">Salir</a>
  </div>
</template>

<script setup>
  /*
   * Aviso de que esto no es la web de verdad.
   *
   * Sin esto una vista previa es indistinguible de lo publicado, y asi es como
   * se acaba ensenandole a alguien un borrador creyendo que ya estaba en
   * cartel, o dando por publicado algo que todavia no lo esta.
   *
   * Lo unico que lee es una cookie que el propio API deja al entrar,
   * `vista_previa_aviso`, con la forma `marca|recurso|caducidad`. No lleva nada
   * que sirva para entrar —la que levanta el filtro es otra, httpOnly— y por
   * eso se puede leer desde aqui.
   *
   * El texto va en castellano y sin traducir a proposito: esto no lo ve un
   * visitante, lo ve quien edita, y el gestor es de la casa.
   */
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

  const COOKIE = 'vista_previa_aviso'

  const testigo = ref(null)
  const ahora = ref(Math.floor(Date.now() / 1000))
  let reloj = null

  const leerCookie = () => {
    const fila = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${COOKIE}=`))

    if (!fila) return null

    const [marca, recurso, caducidad] = decodeURIComponent(
      fila.slice(COOKIE.length + 1)
    ).split('|')

    return { marca, recurso, caducidad: Number(caducidad) || 0 }
  }

  const restante = computed(() =>
    testigo.value ? testigo.value.caducidad - ahora.value : 0
  )

  const visible = computed(() => !!testigo.value && restante.value > 0)

  const QUE = {
    experiencia: 'esta experiencia',
    tipo: 'este tipo',
    banner: 'este banner'
  }

  const explicacion = computed(() => {
    if (!testigo.value) return ''

    const que = QUE[testigo.value.recurso] || 'este contenido'

    return testigo.value.marca === 'borrador'
      ? `Con cambios sin guardar de ${que}. No los ve nadie mas.`
      : `Viendo contenido sin publicar. No lo ve nadie mas.`
  })

  const cuenta = computed(() => {
    const s = Math.max(0, restante.value)

    return `Caduca en ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
  })

  const base = process.env.VUE_APP_PATH || '/'
  const salida = (base.endsWith('/') ? base : `${base}/`) + 'preview/salir'

  /*
   * Salir cierra la pestana, que es de donde no se deberia haber salido nunca:
   * la vista previa se abre en una aparte desde el gestor y lo natural es que
   * al terminar desaparezca y se vuelva al formulario que quedo abierto detras.
   *
   * Primero hay que pedirle al servidor que borre la cookie: es httpOnly —es la
   * que levanta el filtro de publicacion— y desde aqui no se puede tocar. Si se
   * cerrara la pestana sin eso, la sesion de vista previa seguiria viva quince
   * minutos y la siguiente visita a la web seguiria sin ser la web de verdad.
   *
   * Cerrar solo funciona si la pestana la abrio un script, que es el caso de
   * "Previsualizar cambios". La de "Ver en la web" es un enlace normal y el
   * navegador no deja cerrarla: entonces se queda, pero ya en la web publicada.
   */
  const salir = async (evento) => {
    evento.preventDefault()

    try {
      await fetch(salida, { credentials: 'same-origin', redirect: 'manual' })
    } catch {
      // Aunque falle se sigue: mas vale quitar el aviso y salir de aqui que
      // dejar al gestor encerrado en una pagina que dice ser una vista previa.
    }

    testigo.value = null
    window.close()

    if (!window.closed) window.location.assign('/')
  }

  onMounted(() => {
    testigo.value = leerCookie()

    if (!testigo.value) return

    reloj = setInterval(() => {
      ahora.value = Math.floor(Date.now() / 1000)
    }, 1000)
  })

  onBeforeUnmount(() => clearInterval(reloj))
</script>

<style lang="scss" scoped>
  /*
   * Abajo y no arriba: arriba taparia la cabecera de la propia pagina, que es
   * parte de lo que se esta mirando. Fija, porque un aviso que se va con el
   * scroll deja de avisar justo cuando mas rato se lleva mirando.
   */
  .vista-previa {
    position: fixed;
    z-index: 9999;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    border-top: 2px solid #b45309;
    background: #fbbf24;
    box-shadow: 0 -4px 14px rgb(0 0 0 / 18%);
    color: #422006;
    font-size: 13px;
    line-height: 1.35;

    &__texto {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px 8px;
      min-width: 0;
    }

    &__punto {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: #b45309;
    }

    &__cuenta {
      opacity: 0.75;
      font-variant-numeric: tabular-nums;
    }

    &__salir {
      flex: 0 0 auto;
      padding: 5px 12px;
      border-radius: 999px;
      background: #422006;
      color: #fbbf24;
      font-weight: 600;
      text-decoration: none;
    }
  }
</style>
